/**
 * Chart configuration for the Voice Sessions widget.
 *
 * Scoped locally (not shared with the AI Agents Insights widget) so the voice
 * metric keys stay independent of the AI-Agents-insights key union.
 *
 * Units are per-metric and match the timeseries point values produced by
 * `useVoiceSessions`: `totalMinutes` is in minutes, `avgSessionLength` in
 * seconds, `voicemailRate` a percentage. Each `valueFormatter` therefore also
 * doubles as the KPI-tile formatter, keeping tile and chart Y-axis in the same unit.
 */

import { type MetricChartConfig } from '../../utils/chartConfig';
import { formatDurationFromSeconds } from '../../utils/formatDuration';

export type VoiceMetricKey =
  'totalSessions' | 'totalMinutes' | 'avgSessionLength' | 'voicemailRate';

export const VOICE_METRIC_CHART_CONFIGS: Record<VoiceMetricKey, MetricChartConfig> = {
  totalSessions: {
    dataKey: 'value',
    label: 'analytics.voice_total_sessions',
    domain: [0, 'auto'],
    valueFormatter: (value) => value.toLocaleString(),
    fill: 'zero',
  },
  totalMinutes: {
    dataKey: 'value',
    label: 'analytics.voice_total_minutes',
    domain: [0, 'auto'],
    valueFormatter: (minutes) => formatDurationFromSeconds(minutes * 60),
    fill: 'zero',
  },
  avgSessionLength: {
    dataKey: 'value',
    label: 'analytics.voice_avg_session_length',
    domain: [0, 'auto'],
    valueFormatter: formatDurationFromSeconds,
    fill: 'gap',
  },
  voicemailRate: {
    dataKey: 'value',
    label: 'analytics.voice_voicemail_rate',
    domain: [0, 'auto'],
    valueFormatter: (value) => `${Math.round(value)}%`,
    fill: 'gap',
  },
};
